/**
 * Booking command mutations (create / edit / cancel) + the manager step-up mint. On success each
 * invalidates BOTH the bookings family and availability (a claim consumed/released changes open
 * slots) so all four views + the live-slot picker refetch. Errors are NOT swallowed — the dialog
 * inspects them: a 409 stale-write opens the conflict modal (never last-write-wins, §D.3), a typed
 * rejection renders alternatives / the diner-equivalent message (§C.6).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ApproveRequestBody,
  DeclineRequestBody,
  StaffBookingResponse,
  StaffCancelBookingRequest,
  StaffCreateBookingRequest,
  StaffEditBookingRequest,
} from "@repo/api-client";
import { requestStepUp } from "../shell/pinSwitch";
import { bookingsApi } from "./bookingsApi";

function useInvalidateBookings(venueId: string): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["venue", venueId, "bookings"] }),
      qc.invalidateQueries({ queryKey: ["venue", venueId, "availability"] }),
    ]);
  };
}

export function useCreateBooking(venueId: string) {
  const invalidate = useInvalidateBookings(venueId);
  return useMutation<StaffBookingResponse, unknown, StaffCreateBookingRequest>({
    mutationFn: (body) => bookingsApi.create(venueId, body),
    onSuccess: invalidate,
  });
}

export function useEditBooking(venueId: string, bookingId: string) {
  const invalidate = useInvalidateBookings(venueId);
  return useMutation<StaffBookingResponse, unknown, StaffEditBookingRequest>({
    mutationFn: (body) => bookingsApi.edit(venueId, bookingId, body),
    onSuccess: invalidate,
  });
}

export interface CancelVars {
  body: StaffCancelBookingRequest;
  stepUpAuthId?: string;
}

export function useCancelBooking(venueId: string, bookingId: string) {
  const invalidate = useInvalidateBookings(venueId);
  return useMutation<StaffBookingResponse, unknown, CancelVars>({
    mutationFn: ({ body, stepUpAuthId }) =>
      bookingsApi.cancel(venueId, bookingId, body, stepUpAuthId),
    onSuccess: invalidate,
  });
}

/**
 * Approve a PENDING request-to-book booking (request-to-book, R6). On success the booking becomes
 * CONFIRMED and both the bookings family (inbox row drops) and availability (the hold became a claim)
 * are invalidated. A 409 typed rejection / stale-write is surfaced by the caller (never swallowed).
 */
export function useApproveRequest(venueId: string, bookingId: string) {
  const invalidate = useInvalidateBookings(venueId);
  return useMutation<StaffBookingResponse, unknown, ApproveRequestBody>({
    mutationFn: (body) => bookingsApi.approveRequest(venueId, bookingId, body),
    onSuccess: invalidate,
  });
}

/** Decline a PENDING request (releases the hold; optional reason + live-checked proposed alternative). */
export function useDeclineRequest(venueId: string, bookingId: string) {
  const invalidate = useInvalidateBookings(venueId);
  return useMutation<StaffBookingResponse, unknown, DeclineRequestBody>({
    mutationFn: (body) => bookingsApi.declineRequest(venueId, bookingId, body),
    onSuccess: invalidate,
  });
}

/**
 * Mint a single-use `CANCEL_CONFIRMED_BOOKING` authorization from a manager PIN (ST-4). The returned
 * `authorizationId` is passed as the step-up header on the immediately-following cancel. An invalid
 * PIN / lockout surfaces as an `ApiError` the dialog renders.
 */
export function mintCancelStepUp(
  venueId: string,
  pin: string,
  bookingId: string,
): Promise<{ authorizationId: string; expiresAt: string }> {
  return requestStepUp(venueId, {
    pin,
    action: "CANCEL_CONFIRMED_BOOKING",
    targetRef: bookingId,
  });
}
